import type { Metadata } from "next";
import localFont from "next/font/local";
import Link from "next/link";
import { prisma } from "@/lib/prisma";

type BoutiquePageProps = {
  searchParams?: Promise<{
    category?: string;
    glitter?: string;
    q?: string;
  }>;
};

const interFont = localFont({
  src: "../../public/fonts/Inter-VariableFont_opsz,wght.woff2",
  variable: "--font-inter-local",
  display: "swap",
});

const ibmPlexMonoFont = localFont({
  src: "../../public/fonts/IBMPlexMono-Regular.woff2",
  variable: "--font-ibm-plex-mono-local",
  display: "swap",
});

const pagePalette = {
  background:
    "linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)",
} as React.CSSProperties;

export const metadata: Metadata = {
  title: "Boutique | Jour J Création",
  description:
  "Découvrez ma boutique de laines teintes à la main : pelotes uniques, kits 2, 3, 4 et 5 pelotes, avec ou sans paillettes.",
};

function formatPrice(value: number | string) {
  const amount = typeof value === "string" ? Number(value) : value;
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(amount);
}

function formatStockLabel(stock: number) {
  if (stock <= 0) return "Indisponible";
  if (stock === 1) return "Plus qu’1 en stock";
  if (stock === 2) return "Plus que 2 en stock";
  if (stock === 3) return "Plus que 3 en stock";
  return `${stock} en stock`;
}

function normalizeProductLabel(value: string) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function isGlitterProduct(product: {
  name: string;
  shortDescription: string | null;
  category: { name: string; slug: string };
  isGlitter: boolean;
}) {
  const label = normalizeProductLabel(
    `${product.name} ${product.shortDescription ?? ""} ${product.category.name}`,
  );

  return product.isGlitter || label.includes("paillettes") || /\b[1-5]p\d{2}\b/.test(label);
}

function matchesSearch(
  product: {
    name: string;
    shortDescription: string | null;
    category: { name: string; slug: string };
  },
  searchQuery: string,
) {
  if (!searchQuery) return true;

  const haystack = normalizeProductLabel(
    `${product.name} ${product.shortDescription ?? ""} ${product.category.name}`,
  );

  return haystack.includes(normalizeProductLabel(searchQuery));
}

export default async function BoutiquePage({
  searchParams,
}: BoutiquePageProps) {
  const params = (await searchParams) ?? {};
  const selectedCategory = params.category ?? "all";
  const selectedGlitter = params.glitter ?? "all";
  const searchQuery = (params.q ?? "").trim();

  const categories = await prisma.category.findMany({
    where: { isVisible: true },
    orderBy: { sortOrder: "asc" },
  });

  const allProducts = await prisma.product.findMany({
    where: { isPublished: true },
    include: {
      category: true,
      images: {
        orderBy: { sortOrder: "asc" },
        take: 1,
      },
    },
    orderBy: [{ isFeatured: "desc" }, { createdAt: "desc" }],
  });

  const products = allProducts.filter((product) => {
    const matchesCategory =
      selectedCategory === "all" ? true : product.category.slug === selectedCategory;

    const matchesGlitter =
      selectedGlitter === "all"
        ? true
        : selectedGlitter === "glitter"
          ? isGlitterProduct(product)
          : !isGlitterProduct(product);

    return matchesCategory && matchesGlitter && matchesSearch(product, searchQuery);
  });

  const totalProducts = products.length;

  return (
    <div style={pagePalette} className={interFont.className}>
      <section className="section pt-4 sm:pt-5 lg:pt-6">
        <div className="mx-auto max-w-[1500px] px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-14">
          <div className="grid gap-5 lg:grid-cols-[262px_minmax(0,1fr)] lg:items-start xl:grid-cols-[278px_minmax(0,1fr)] xl:gap-7">
            <FinderSection
              selectedCategory={selectedCategory}
              selectedGlitter={selectedGlitter}
              searchQuery={searchQuery}
              totalProducts={totalProducts}
            />

            <div className="min-w-0">
              <ProductsSection products={products} withinLayout />
              <SeoSection withinLayout />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function buildFilterHref(category: string, glitter: string, q = "") {
  const params = new URLSearchParams();

  if (category !== "all") params.set("category", category);
  if (glitter !== "all") params.set("glitter", glitter);
  if (q.trim()) params.set("q", q.trim());

  const query = params.toString();
  return query ? `/boutique?${query}` : "/boutique";
}

function FinderSection({
  selectedCategory,
  selectedGlitter,
  searchQuery,
  totalProducts,
}: {
  selectedCategory: string;
  selectedGlitter: string;
  searchQuery: string;
  totalProducts: number;
}) {
  const formatLinks = [
    {
      label: "Pelote unique",
      value: "pelotes-uniques",
      href: buildFilterHref("pelotes-uniques", "all", searchQuery),
      image: "/images/1pelotte.webp",
    },
    {
      label: "Kit 2 pelotes",
      value: "kits-2-pelotes",
      href: buildFilterHref("kits-2-pelotes", "all", searchQuery),
      image: "/images/2pelotte.webp",
    },
    {
      label: "Kit 3 pelotes",
      value: "kits-3-pelotes",
      href: buildFilterHref("kits-3-pelotes", "all", searchQuery),
      image: "/images/3pelotte.webp",
    },
    {
      label: "Kit 4 pelotes",
      value: "kits-4-pelotes",
      href: buildFilterHref("kits-4-pelotes", "all", searchQuery),
      image: "/images/4pelotte.webp",
    },
    {
      label: "Kit 5 pelotes",
      value: "kits-5-pelotes",
      href: buildFilterHref("kits-5-pelotes", "all", searchQuery),
      image: "/images/5pelotte.webp",
    },
  ];

  const finishLinks = [
    { label: "Sans paillettes", value: "plain", href: buildFilterHref(selectedCategory, "plain", searchQuery) },
    { label: "À paillettes", value: "glitter", href: buildFilterHref(selectedCategory, "glitter", searchQuery) },
  ];

  return (
    <>
      <aside className="hidden lg:block lg:sticky lg:top-[132px] lg:self-start">
        <div className="overflow-hidden rounded-[24px] border border-[var(--border)] bg-[#f4d7e7] shadow-[0_16px_38px_rgba(50,35,35,0.065)]">
          <div className="border-b border-[var(--border)] px-4 py-4">
            <p className={`${ibmPlexMonoFont.className} text-[10px] uppercase tracking-[0.2em] text-[var(--accent)]/82`}>
              Navigation boutique
            </p>
            <div className="mt-2.5 flex items-center justify-between gap-2.5">
              <h2 className="text-[1.28rem] leading-[0.98] text-[var(--foreground)]">
                Filtres
              </h2>
              <span className="rounded-full border border-[var(--border)] bg-white/40 px-2.5 py-0.5 text-[11px] text-[var(--foreground)]/68">
                {totalProducts} résultat{totalProducts > 1 ? "s" : ""}
              </span>
            </div>
            <p className="mt-2 max-w-[21ch] text-[12px] leading-5 text-[var(--foreground)]/62">
              Sélectionnez un format, puis une finition pour affiner la boutique.
            </p>
          </div>

          <div className="px-4 py-4">
            <div>
              <p className={`${ibmPlexMonoFont.className} text-[10px] uppercase tracking-[0.2em] text-[var(--accent)]/82`}>
                Format
              </p>
              <div className="mt-2.5 space-y-2">
                {formatLinks.map((item) => {
                  const isActive = selectedCategory === item.value;

                  return (
                    <Link
                      key={item.value}
                      href={item.href}
                      className={[
                        "group flex items-center justify-between rounded-[14px] border px-3.5 py-2.5 text-[13px] transition duration-300",
                        isActive
                          ? "border-[var(--primary)] bg-white/50 shadow-[0_10px_22px_rgba(50,35,35,0.05)]"
                          : "border-[var(--border)] bg-white/24 hover:-translate-y-0.5 hover:border-[var(--primary)] hover:bg-white/34",
                      ].join(" ")}
                    >
                      <span className="flex items-center gap-2.5">
                        <span className="inline-flex h-8 w-8 items-center justify-center overflow-hidden rounded-full border border-[var(--border)] bg-white/40">
    <img
      src={item.image}
      alt=""
      aria-hidden="true"
      className="h-full w-full object-cover"
    />
                        </span>
                        <span>{item.label}</span>
                      </span>
<span className="text-[var(--foreground)]/34 transition duration-300 group-hover:text-[var(--primary)]">→</span>
                    </Link>
                  );
                })}
              </div>
            </div>

            {selectedCategory !== "all" ? (
              <div className="mt-4 border-t border-[var(--border)] pt-4">
                <p className={`${ibmPlexMonoFont.className} text-[10px] uppercase tracking-[0.2em] text-[var(--accent)]/82`}>
                  Finition
                </p>
                <div className="mt-2.5 space-y-2">
                  {finishLinks.map((item) => {
                    const isActive = selectedGlitter === item.value;

                    return (
                      <Link
                        key={item.value}
                        href={item.href}
                        className={[
                          "group flex items-center justify-between rounded-[14px] border px-3.5 py-2.5 text-[13px] transition duration-300",
                          isActive
                            ? "border-[var(--primary)] bg-white/50 shadow-[0_10px_22px_rgba(50,35,35,0.05)]"
                            : "border-[var(--border)] bg-white/24 hover:-translate-y-0.5 hover:border-[var(--primary)] hover:bg-white/34",
                        ].join(" ")}
                      >
                        <span>{item.label}</span>
                        <span className="text-[var(--foreground)]/34 transition duration-300 group-hover:text-[var(--primary)]">→</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ) : null}

            <div className="mt-4 border-t border-[var(--border)] pt-4">
              <Link
                href="/boutique"
                className="inline-flex w-full items-center justify-center rounded-full bg-[var(--primary)] px-4 py-2.5 text-sm font-medium text-white shadow-[0_14px_30px_rgba(255,63,142,0.18)] transition duration-300 hover:-translate-y-0.5 hover:opacity-95"
              >
                Réinitialiser
              </Link>
            </div>
          </div>
        </div>
      </aside>

      <div className="lg:hidden">
      <details className="group overflow-hidden rounded-[18px] border border-[var(--border)] bg-[#f4d7e7] shadow-[0_12px_24px_rgba(50,35,35,0.045)]">
      <summary className="flex cursor-pointer list-none items-start justify-between gap-3 px-4 py-2 marker:content-none">
      <div>
        <p className={`${ibmPlexMonoFont.className} text-[10px] uppercase tracking-[0.22em] text-[var(--accent)]/82`}>
          Navigation boutique
        </p>
        <div className="mt-0.5 flex items-center gap-2">
        <span className="text-[1rem] leading-none text-[var(--foreground)]">
            Filtres
          </span>
          <span className="rounded-full border border-[var(--border)] bg-white/40 px-2.5 py-0.5 text-[10px] text-[var(--foreground)]/68">
            {totalProducts} résultat{totalProducts > 1 ? "s" : ""}
          </span>
        </div>
      </div>

      <p className="max-w-[22ch] text-[12px] leading-5 text-[var(--foreground)]/62">
        Sélectionnez un format, puis une finition.
      </p>

      <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[var(--border)] bg-white/34 text-[var(--foreground)]/72 transition duration-300 group-open:rotate-180 group-open:border-[var(--primary)] group-open:text-[var(--primary)]">
        ↓
      </span>
    </summary>

    <div className="border-t border-[var(--border)] px-4 py-3">
      <div>
        <p className={`${ibmPlexMonoFont.className} text-[10px] uppercase tracking-[0.22em] text-[var(--accent)]/82`}>
          Format
        </p>

        <div className="mt-2 space-y-1.5">
          {formatLinks.map((item) => {
            const isActive = selectedCategory === item.value;

            return (
              <Link
                key={`mobile-${item.value}`}
                href={item.href}
                className={[
"group flex items-center justify-between rounded-[13px] border px-3.5 py-2 text-[13px] transition duration-300",
                  isActive
                    ? "border-[var(--primary)] bg-white/50 shadow-[0_10px_22px_rgba(50,35,35,0.05)]"
                    : "border-[var(--border)] bg-white/24 hover:border-[var(--primary)] hover:bg-white/34",
                ].join(" ")}
              >
            <span className="flex items-center gap-2.5">
            <span className="inline-flex h-8 w-8 items-center justify-center overflow-hidden rounded-full border border-[var(--border)] bg-white/40">
    <img
      src={item.image}
      alt=""
      aria-hidden="true"
      className="h-full w-full object-cover"
    />
              </span>
              <span>{item.label}</span>
            </span>
<span className="text-[var(--foreground)]/34 transition duration-300 group-hover:text-[var(--primary)]">
  →
</span>
              </Link>
            );
          })}
        </div>
      </div>

      {selectedCategory !== "all" ? (
        <div className="mt-3.5 border-t border-[var(--border)] pt-3.5">
          <p className={`${ibmPlexMonoFont.className} text-[10px] uppercase tracking-[0.22em] text-[var(--accent)]/82`}>
            Finition
          </p>

          <div className="mt-2 space-y-1.5">
            {finishLinks.map((item) => {
              const isActive = selectedGlitter === item.value;

              return (
                <Link
                  key={`mobile-${item.value}`}
                  href={item.href}
                  className={[
"group flex items-center justify-between rounded-[13px] border px-3.5 py-2 text-[13px] transition duration-300",
                    isActive
                      ? "border-[var(--primary)] bg-white/50 shadow-[0_10px_22px_rgba(50,35,35,0.05)]"
                      : "border-[var(--border)] bg-white/24 hover:border-[var(--primary)] hover:bg-white/34",
                  ].join(" ")}
                >
                  <span>{item.label}</span>
                  <span className="text-[var(--foreground)]/34 transition duration-300 group-hover:text-[var(--primary)]">
                    →
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      ) : null}

<div className="mt-3.5 border-t border-[var(--border)] pt-3.5">
        <Link
          href="/boutique"
className="inline-flex w-full items-center justify-center rounded-full bg-[var(--primary)] px-4 py-2 text-sm font-medium text-white shadow-[0_14px_30px_rgba(255,63,142,0.18)] transition duration-300 hover:opacity-95"
        >
          Réinitialiser
        </Link>
      </div>
    </div>
  </details>
</div>
    </>
  );
}
function StepPill({
  index,
  label,
  active,
  completed,
}: {
  index: string;
  label: string;
  active: boolean;
  completed: boolean;
}) {
  return (
    <div
      className={[
        "inline-flex items-center gap-3 rounded-full border px-4 py-2 text-sm transition duration-300",
        "border-[var(--border)] bg-[#f4d7e7] text-[var(--foreground)] hover:border-[var(--primary)]",
        active ? "border-[var(--primary)] shadow-[var(--shadow)]" : completed ? "text-[var(--foreground)]/80 hover:-translate-y-0.5" : "text-[var(--foreground)]/72",
      ].join(" ")}
    >
      <span className={`${ibmPlexMonoFont.className} text-[11px] uppercase tracking-[0.18em]`}>{index}</span>
      <span>{label}</span>
    </div>
  );
}

function FinderCard({
  title,
  desc,
  icon,
}: {
  title: string;
  desc: string;
  icon: "single" | "duo" | "trio" | "plain" | "sparkle";
}) {
  return (
    <div className="group relative h-full overflow-hidden rounded-[20px] border border-[var(--border)] bg-[#f4d7e7] p-2.5 transition duration-500 hover:-translate-y-1.5 hover:border-[var(--primary)] hover:shadow-[var(--shadow)] sm:p-3">
    <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.06),transparent_26%),radial-gradient(circle_at_18%_14%,rgba(244,215,231,0.10),transparent_24%),radial-gradient(circle_at_84%_82%,rgba(209,15,115,0.04),transparent_20%)] opacity-80 transition duration-500 group-hover:scale-[1.04]" />

      <div className="relative z-[1] flex h-full flex-col">
      <div className="mb-2 inline-flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-[#f4d7e7] text-[var(--foreground)]">
      {icon === "single" && (
  <img
    src="/images/1pelotte.webp"
    alt=""
    aria-hidden="true"
    className="h-full w-full object-cover"
  />
)}

{icon === "duo" && (
  <img
    src="/images/2pelotte.webp"
    alt=""
    aria-hidden="true"
    className="h-full w-full object-cover"
  />
)}

{icon === "trio" && (
  <img
    src="/images/3pelotte.webp"
    alt=""
    aria-hidden="true"
    className="h-full w-full object-cover"
  />
)}

          {icon === "plain" && (
            <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5 fill-none stroke-current stroke-[1.8]">
              <path d="M5 12h14" />
              <path d="M7 8h10" />
              <path d="M7 16h10" />
            </svg>
          )}

          {icon === "sparkle" && (
            <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5 fill-none stroke-current stroke-[1.8]">
              <path d="M12 4v4" />
              <path d="M12 16v4" />
              <path d="M4 12h4" />
              <path d="M16 12h4" />
              <path d="M7 7l2.2 2.2" />
              <path d="M14.8 14.8 17 17" />
              <path d="M17 7l-2.2 2.2" />
              <path d="M9.2 14.8 7 17" />
            </svg>
          )}
        </div>

        <h2 className="text-[1.08rem] leading-[1.05] text-[var(--foreground)] sm:text-[1.22rem]">
          {title}
        </h2>

        <p className="mt-0.5 text-[12px] leading-4 text-[var(--foreground)]/72">
          {desc}
        </p>

        <div className="mt-2 flex items-center justify-between gap-2.5">
          <span className="text-[13px] text-[var(--foreground)]/56 transition duration-300 group-hover:text-[var(--foreground)]/82">
            Continuer
          </span>

          <div className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#f4d7e7] text-[1.35rem] text-[var(--foreground)] transition duration-300 group-hover:translate-x-1 group-hover:bg-[var(--primary)] group-hover:text-white">
            →
          </div>
        </div>
      </div>
    </div>
  );
}


function ProductsSection({
  products,
  withinLayout = false,
}: {
  products: Array<{
    id: string;
    name: string;
    slug: string;
    shortDescription: string | null;
    price: unknown;
    isGlitter: boolean;
    stock: number;
    category: {
      name: string;
      slug: string;
    };
    images: Array<{
      imageUrl: string;
      alt: string | null;
    }>;
  }>;
  withinLayout?: boolean;
}) {
  return (
    <section
      id="catalogue-resultats"
      className={withinLayout ? "scroll-mt-20 pt-0" : "section scroll-mt-20 pt-4 sm:pt-6"}
    >
      <div className={withinLayout ? "w-full" : "mx-auto max-w-[1400px] px-6 sm:px-8 lg:px-10 xl:px-14"}>
        <div className="mb-6 h-px bg-gradient-to-r from-transparent via-[var(--border)] to-transparent" />
        {products.length === 0 ? (
          <div className="rounded-[30px] border border-[var(--border)] bg-[#f4d7e7] p-8 text-center shadow-[var(--shadow)] sm:p-12">
            <p className={`${ibmPlexMonoFont.className} text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85`}>
              Aucune correspondance
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl">
              Aucun produit ne correspond à ces filtres pour le moment.
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-[var(--foreground)]/72 sm:text-base">
              Réinitialisez la recherche ou les filtres pour retrouver l’ensemble de la sélection.
            </p>
            <div className="mt-8">
              <Link
                href="/boutique"
                className="rounded-full bg-[var(--primary)] px-6 py-3 text-sm font-medium text-white transition hover:opacity-90"
              >
                Réinitialiser les filtres
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3">
            {products.map((product) => {
              const image = product.images[0];

              return (
                <Link
                  key={product.id}
                  href={`/produit/${product.slug}`}
                  className="group block h-full"
                >
                  <article className="flex h-full min-h-[100%] flex-col rounded-[30px] border border-[var(--border)] bg-[#f4d7e7] p-5 shadow-[var(--shadow)] transition duration-500 hover:-translate-y-1.5 hover:shadow-[0_26px_60px_rgba(50,35,35,0.08)]">
                    <div className="flex items-start justify-between gap-4 border-b border-[var(--border)]/70 pb-4">
                    <span className={`${ibmPlexMonoFont.className} text-[11px] uppercase tracking-[0.18em] text-[var(--primary)]/92`}>
                        {product.category.name}
                      </span>

                      {isGlitterProduct(product) ? (
                        <span className="rounded-full bg-[var(--primary)] px-3 py-1 text-[11px] font-medium text-white shadow-[0_10px_24px_rgba(255,63,142,0.14)]">
                          À paillettes
                        </span>
                      ) : (
                        <span className="rounded-full border border-[rgba(217,90,47,0.18)] bg-white/34 px-3 py-1 text-[11px] text-[var(--foreground)]/60">
                          Sans paillettes
                        </span>
                      )}
                    </div>
                    <div className="mt-5 overflow-hidden rounded-[24px] bg-[#f4d7e7]">
                      {image?.imageUrl ? (
                        <div className="aspect-[5/4] overflow-hidden rounded-[24px]">
                          <img
                            src={image.imageUrl}
                            alt={image.alt || product.name}
                            className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.05]"
                          />
                        </div>
                      ) : (
                        <div className="aspect-[5/4] rounded-[24px] bg-[#f4d7e7]" />
                      )}
                    </div>
                    <div className="mt-7 flex flex-1 flex-col">
                      <h3 className="text-[1.7rem] leading-[1.04]">{product.name}</h3>

                      <p className="mt-3 min-h-[72px] text-sm leading-6 text-[var(--foreground)]/72">
                        {product.shortDescription ||
                          "Une création teinte à la main, pensée pour offrir une vraie présence visuelle et une belle sensibilité de couleur."}
                      </p>

                      <div className="mt-6 flex items-end justify-between gap-4">
                        <div>
                        <p className={`${ibmPlexMonoFont.className} text-[11px] uppercase tracking-[0.18em] text-[var(--primary)]/72`}>
                            Prix
                          </p>
                          <p className="mt-1 text-[1.6rem] font-semibold tracking-[-0.02em] text-[var(--primary)] drop-shadow-[0_8px_18px_rgba(255,63,142,0.10)]">
                            {formatPrice(String(product.price))}
                          </p>
                        </div>

                        <div className="text-right">
                        <p className={`${ibmPlexMonoFont.className} text-[11px] uppercase tracking-[0.18em] text-[var(--foreground)]/50`}>
                            Stock
                          </p>
                          <p
                            className={[
                              "mt-1 text-sm",
                              product.stock > 0
                                ? "text-[var(--primary)]"
                                : "text-[var(--foreground)]/72",
                            ].join(" ")}
                          >
                            {formatStockLabel(product.stock)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </article>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

function SeoSection({ withinLayout = false }: { withinLayout?: boolean }) {
  return (
    <section className={withinLayout ? "pt-8" : "section pt-0"}>
      <div className={withinLayout ? "w-full" : "mx-auto max-w-[1400px] px-6 sm:px-8 lg:px-10 xl:px-14"}>
        <div className="rounded-[34px] border border-[var(--border)] bg-[#f4d7e7] p-8 shadow-[0_24px_60px_rgba(50,35,35,0.06)] sm:p-10">
          <div className="max-w-3xl">
          <p className={`${ibmPlexMonoFont.className} text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85`}>
              À propos de la boutique
            </p>

            <h2 className="mt-4 text-4xl leading-[1.02] sm:text-5xl">
              Une boutique de laines teintes artisanalement pensée pour guider le choix avec clarté.
            </h2>

            <div className="mt-6 space-y-5 text-base leading-7 text-[var(--foreground)]/74">
              <p>
                Cette boutique rassemble mes laines teintes à la main sous plusieurs formats :
                pelotes uniques, kits de 2 pelotes, kits de 3 pelotes, kits de 4 pelotes et
                kits de 5 pelotes. L’objectif est de permettre une lecture simple de la collection
                tout en laissant apparaître la richesse des harmonies, des nuances et des matières.
              </p>

              <p>
                Les produits peuvent également être distingués entre laines à paillettes et
                laines sans paillettes, selon la présence éventuelle de lurex ou de stellina.
                Cette organisation aide à choisir plus justement une laine mérinos superwash
                fingering artisanale adaptée à un projet précis, à une palette ou à un style
                d’ouvrage.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}